import { Link } from "react-router-dom";
import { ChevronDown, Menu } from "lucide-react";
import { getImageUrl, getSubdomain, isRootDomain, linkTo, getBaseUrl, getPublicUrl } from '@/lib/utils';
import { useTenant } from '@/contexts/TenantContext';
import React, { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

interface Tenant {
  id: string;
  name: string;
  logo_url?: string;
  color_scheme_json?: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    text: string;
  };
}

interface StickyNavigationProps {
	tenant?: Tenant;
	// Optional per-page overrides
	headerPaddingClasses?: string;      // e.g., "py-3 sm:py-4" (defaults to "py-2 sm:py-3")
	logoSizeDesktopClass?: string;      // e.g., "h-[48px]" (defaults to computed)
	logoSizeMobileClass?: string;       // e.g., "h-[38px]" (defaults to computed)
}

const StickyNavigation = ({ tenant, headerPaddingClasses, logoSizeDesktopClass, logoSizeMobileClass }: StickyNavigationProps) => {
  // const location = useLocation();
  const isMainSite = isRootDomain();
  const { tenant: tenantCtx } = useTenant();
  const t: any = tenant || tenantCtx;
  const isSubDomain = t?.subdomain === getSubdomain();
  // Fallback detect from host when TenantContext is not loaded (e.g. localhost without .env domains)
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const hostSub = host.split('.')[0] || '';
	const pathIsIncorpifyAI = typeof window !== 'undefined' && /(?:^|\/)(?:business\/)?site\/incorpify-ai(?:\/|$)/.test(window.location.pathname);
	const isIncorpifyAI = (t?.subdomain === "incorpify-ai") || (hostSub === 'incorpify-ai') || pathIsIncorpifyAI;
	const isDCC = (t?.subdomain === 'dubaicommercity') || (hostSub === 'dubaicommercity') || (typeof window !== 'undefined' && /(?:^|\/)site\/dubaicommercity(?:\/|$)/.test(window.location.pathname));
	const isVirtuzone = (t?.subdomain === 'virtuzone') || (hostSub === 'virtuzone') || (typeof window !== 'undefined' && /(?:^|\/)(?:business\/)?site\/virtuzone(?:\/|$)/.test(window.location.pathname));

	// DCC logo: probe known file names in /public/Logos/dcc and pick the first that loads
	const [dccLogo, setDccLogo] = useState<string | undefined>(undefined);
	useEffect(() => {
	  if (!isDCC) return;
	  const ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
	  const ABS_FIXED = ORIGIN ? `${ORIGIN}/Logos/dcc/dcc%20logo/dcc.png` : getPublicUrl('/Logos/dcc/dcc%20logo/dcc.png');
	  setDccLogo(ABS_FIXED);
	  return;
	}, [isDCC]);

	// Mobile nav state (hamburger -> sheet)
	const [mobileOpen, setMobileOpen] = useState(false);


  // Prefer explicit navbar colors if provided; also support prop-based data from TenantIndex
  const colorScheme: any = (tenantCtx as any)?.colorScheme || (t as any)?.color_scheme_json || (t as any)?.colorScheme;
  const navbarBg = colorScheme?.navbarBg;
  const navbarText = colorScheme?.navbarText;
  const navbarHoverText = colorScheme?.navbarHoverText;

  const bannerConfig: any = (tenantCtx as any)?.bannerConfig || (t as any)?.banner_config_json || (t as any)?.bannerConfig;
  const bannerBg = bannerConfig?.backgroundColor;
  const bannerText = bannerConfig?.textColor;
  const defaultBannerBg = '#ffffff';
	  const defaultBannerText = '#1f2937';
	  // Force navbar to white regardless of banner/tenant config
	  const bgColor = '#ffffff';
	  const textColor = '#1f2937';

  // Use public/ assets with base-aware URLs so they resolve correctly under /business and root
  const INCORPIFY_LOGO = getPublicUrl(`/Logos/incorpify/inc-logo.png`);
  const VZ_LOGO = getPublicUrl(`/Logos/virtuzone.png`);
  const PRIMARY_MAIN_LOGO = "https://mborlyertmbornltlmdf.supabase.co/storage/v1/object/public/insurer-logos/YC4B.png";
  const SUPABASE_NEW_LOGO = "https://mborlyertmbornltlmdf.supabase.co/storage/v1/object/public/insurer-logos/newlogo.png";
	  const DEFAULT_LOGO = getImageUrl("yallacompare-for-business-logo.png");
  const ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
  const DCC_LOGO_ABS = ORIGIN ? `${ORIGIN}/Logos/dcc/dcc%20logo/dcc.png` : getPublicUrl(`/Logos/dcc/dcc%20logo/dcc.png`);
  const DCC_LOGO = getPublicUrl(`/Logos/dcc/dcc%20logo/dcc.png`);
  const logoFromProp = (t as any)?.logo_url || (t as any)?.logoUrl;
	  let preferredLogo = logoFromProp || (tenantCtx as any)?.logoUrl;
	  // Incorpify / Incorpify-AI: always use the static Incorpify logo, regardless of DB logoUrl
	  if (isIncorpifyAI) {
	    preferredLogo = INCORPIFY_LOGO;
	  }
		  // DCC: force the PNG only (no fallbacks) – but never override Incorpify-AI host
		  else if (isDCC) {
	    preferredLogo = DCC_LOGO_ABS;
	  }
	  // Always prefer tenant-provided (or forced) logo when available
	  const logoUrl = preferredLogo || (isIncorpifyAI ? INCORPIFY_LOGO : (isDCC ? DCC_LOGO : (isVirtuzone ? VZ_LOGO : PRIMARY_MAIN_LOGO)));
  if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost'))) {
    // eslint-disable-next-line no-console
    console.log('[StickyNavigation] logo', { logoUrl, preferredLogo, tName: (t as any)?.name, sub: (t as any)?.subdomain, MARKER: 'dcc-hard-local-public' });
  }
  const logoAlt = isMainSite ? "YallaCompare for Business" : (isIncorpifyAI ? 'Incorpify' : ((t as any)?.name || (tenantCtx as any)?.name || ''));
  // Standardize navbar logo height across all tenants to keep a consistent header height
  const logoClassNameForDesktop = 'h-[48px]';
  const effectiveLogoDesktop = logoSizeDesktopClass || logoClassNameForDesktop;
  // Keep intrinsic mobile image height modest to preserve header height,
  // and visually enlarge specific tenants via transform scaling (does not affect layout).
  const logoClassNameForMobile = 'h-[38.4px]';
  const effectiveLogoMobile = logoSizeMobileClass || logoClassNameForMobile;

  // Treat main site accurately in dev (localhost without /site/:sub)
  const pathHasSitePrefix = typeof window !== 'undefined' && /(?:^|\/)(?:business)?\/site\//.test(window.location.pathname);
  const isMainMobile = isMainSite || (!tenantCtx?.subdomain && !pathHasSitePrefix);

	  // Tenant-specific mobile sizing: avoid any upscaling to keep logos compact
	  const mobileLogoExtras = isIncorpifyAI ? 'max-w-[70%]' : 'max-w-[60%]';

	  // Prevent visual overflow creeping into the banner on Incorpify mobile only
	  const headerContainerExtras = isIncorpifyAI ? 'overflow-hidden sm:overflow-visible' : '';

  return (
    <header className="shadow-sm border-b sticky top-0 z-50" style={{ backgroundColor: bgColor, color: textColor }}>
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center ${headerPaddingClasses || 'py-3 sm:py-4'} ${headerContainerExtras}`}>
        {/* Desktop layout - grid with centered nav; prevents overlap regardless of logo width */}
        <div className="hidden sm:grid w-full grid-cols-[1fr_auto_1fr] items-center">
          {/* Left: Logo */}
          <div className="flex items-center space-x-3 justify-start">
            <Link to={linkTo('/')} aria-label="Go to homepage" className="block">
              <img
                src={logoUrl}
                alt={logoAlt}
                className={`cursor-pointer object-contain ${effectiveLogoDesktop} w-auto max-w-[260px]`}
                onError={(e) => {
                  const img = e.currentTarget as HTMLImageElement;
                  console.error('[StickyNavigation] logo onError', { src: (e.currentTarget as HTMLImageElement).src });
                  if (!img.dataset.tryDccNewPng) {
                    img.dataset.tryDccNewPng = '1';
                    img.src = getPublicUrl('/Logos/dcc/dcc%20logo/dcc.png');
                    return;
                  }
                  if (!img.dataset.tryDccNewJpg) {
                    img.dataset.tryDccNewJpg = '1';
                    img.src = getPublicUrl('/Logos/dcc/dcc%20logo/dcc.jpg');
                    return;
                  }
                  // Stop fallback for DCC after trying new PNG/JPG to avoid loading the old SVG
                  if (isDCC) {
                    img.onerror = null;
                    return;
                  }
                  // For DCC, always stick to the fixed PNG and stop here
                  if (isDCC) {
                    img.onerror = null;
                    img.src = DCC_LOGO_ABS;
                    return;
                  }
                  // Non-DCC generic fallbacks
                  if (!img.dataset.tryBase) {
                    img.dataset.tryBase = '1';
                    img.src = getPublicUrl('/Logos/new/newlogo.png');
                    return;
                  }
                  if (!img.dataset.tryRoot) {
                    img.dataset.tryRoot = '1';
                    img.src = '/Logos/new/newlogo.png';
                    return;
                  }
                  if (!img.dataset.trySupabase) {
                    img.dataset.trySupabase = '1';
                    img.src = SUPABASE_NEW_LOGO;
                    return;
                  }
                  img.onerror = null;
                  img.src = DEFAULT_LOGO;
                }}
              />
            </Link>
          </div>

	          {/* Center: Navigation Menu */}
	          <nav className="flex items-center justify-center space-x-8 whitespace-nowrap">
	            <div className="relative group">
	              <button className="font-medium flex items-center opacity-90 hover:opacity-100 whitespace-nowrap" style={{ color: '#1f2937' }}>
                Products
                <ChevronDown className="ml-1 h-4 w-4" />
              </button>
              <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="p-2">
                  <Link to={linkTo('/products/property-insurance')} className="block px-3 py-2 text-sm text-gray-900 hover:bg-gray-100 rounded">Property Insurance</Link>
                  <Link to={linkTo('/products/liability-coverage')} className="block px-3 py-2 text-sm text-gray-900 hover:bg-gray-100 rounded">Liability Coverage</Link>
                  <Link to={linkTo('/products/employee-benefits')} className="block px-3 py-2 text-sm text-gray-900 hover:bg-gray-100 rounded">Employee Benefits</Link>
                  <Link to={linkTo('/products/cyber-protection')} className="block px-3 py-2 text-sm text-gray-900 hover:bg-gray-100 rounded">Cyber Protection</Link>
                  <Link to={linkTo('/products/fleet-insurance')} className="block px-3 py-2 text-sm text-gray-900 hover:bg-gray-100 rounded">Fleet Insurance</Link>
                  <Link to={linkTo('/products/professional-indemnity')} className="block px-3 py-2 text-sm text-gray-900 hover:bg-gray-100 rounded">Professional Indemnity</Link>
                  <Link to={linkTo('/products/business-interruption')} className="block px-3 py-2 text-sm text-gray-900 hover:bg-gray-100 rounded">Business Interruption</Link>
                  <Link to={linkTo('/products/workers-compensation')} className="block px-3 py-2 text-sm text-gray-900 hover:bg-gray-100 rounded">Workers Compensation</Link>
                  <Link to={linkTo('/products/product-liability')} className="block px-3 py-2 text-sm text-gray-900 hover:bg-gray-100 rounded">Product Liability</Link>
                </div>
              </div>
            </div>
	            <Link to={linkTo('/decision-tree')} className={`font-medium whitespace-nowrap opacity-90 hover:opacity-100`} style={{ color: '#1f2937' }}>Decision Matrix</Link>
	            <Link to={linkTo('/chronicle')} className={`font-medium whitespace-nowrap opacity-90 hover:opacity-100`} style={{ color: '#1f2937' }}>The Chronicle</Link>
	            <Link to={linkTo('/about-us')} className={`font-medium whitespace-nowrap opacity-90 hover:opacity-100`} style={{ color: '#1f2937' }}>About Us</Link>
	            <Link to={linkTo('/contact-us')} className={`font-medium whitespace-nowrap opacity-90 hover:opacity-100`} style={{ color: '#1f2937' }}>Contact Us</Link>
	            <a href="https://yallacompare.com/" target="_blank" rel="noopener noreferrer" className={`font-medium whitespace-nowrap opacity-90 hover:opacity-100`} style={{ color: '#1f2937' }}>Personal Insurance</a>
          </nav>

          {/* Right: spacer to balance grid */}
          <div />
        </div>

	        {/* Mobile layout - centered logo (2.5x), hamburger on right; header height unchanged */}
	        <div className="sm:hidden flex items-center justify-between w-full relative">
	          <Link to={linkTo('/')} aria-label="Go to homepage" className="block absolute left-1/2 -translate-x-1/2">
	            <img
	              src={logoUrl}
	              alt={logoAlt}
	              className={`cursor-pointer object-contain ${effectiveLogoMobile} w-auto ${mobileLogoExtras} flex-shrink-0 transform origin-center scale-[2.2]`}
	              onError={(e) => {
	                const img = e.currentTarget as HTMLImageElement;
	                console.error('[StickyNavigation] logo onError (mobile)', { src: (e.currentTarget as HTMLImageElement).src });
	                if (!img.dataset.tryDccNewPng) {
	                  img.dataset.tryDccNewPng = '1';
	                  img.src = getPublicUrl('/Logos/dcc/dcc%20logo/dcc.png');
	                  return;
	                }
	                if (!img.dataset.tryDccNewJpg) {
	                  img.dataset.tryDccNewJpg = '1';
	                  img.src = getPublicUrl('/Logos/dcc/dcc%20logo/dcc.jpg');
	                  return;
	                }
	                // Stop fallback for DCC after trying new PNG/JPG to avoid loading the old SVG
	                if (isDCC) {
	                  img.onerror = null;
	                  return;
	                }
	                if (!img.dataset.tryDccJpeg) {
	                  img.dataset.tryDccJpeg = '1';
	                  img.src = getPublicUrl('/Logos/dcc/logo-dcc.jpeg');
	                  return;
	                }
	                if (!img.dataset.tryDccJpg) {
	                  img.dataset.tryDccJpg = '1';
	                  img.src = getPublicUrl('/Logos/dcc/logo-dcc.jpg');
	                  return;
	                }
	                if (!img.dataset.tryDccPng) {
	                  img.dataset.tryDccPng = '1';
	                  img.src = getPublicUrl('/Logos/dcc/logo-dcc.png');
	                  return;
	                }
	                // For DCC, always stick to the fixed PNG and stop here
	                if (isDCC) {
	                  img.onerror = null;
	                  img.src = DCC_LOGO_ABS;
	                  return;
	                }
	                // Non-DCC generic fallbacks
	                if (!img.dataset.tryBase) {
	                  img.dataset.tryBase = '1';
	                  img.src = getPublicUrl('/Logos/new/newlogo.png');
	                  return;
	                }
	                if (!img.dataset.tryRoot) {
	                  img.dataset.tryRoot = '1';
	                  img.src = '/Logos/new/newlogo.png';
	                  return;
	                }
	                if (!img.dataset.trySupabase) {
	                  img.dataset.trySupabase = '1';
	                  img.src = SUPABASE_NEW_LOGO;
	                  return;
	                }
	                img.onerror = null;
	                img.src = DEFAULT_LOGO;
	              }}
	            />
	          </Link>
	
	          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
	            <SheetTrigger asChild>
	              <button aria-label="Open menu" className="p-2 rounded-md border border-gray-200 text-gray-900">
	                <Menu className="h-6 w-6" />
	              </button>
	            </SheetTrigger>
	            <SheetContent side="right" className="p-0 bg-white text-gray-900" style={{ backgroundColor: '#ffffff', color: '#111827' }}>
	              <nav className="px-2 py-3 bg-white text-gray-900">
	                <Link to={linkTo('/')} onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-base text-gray-900 hover:bg-gray-50 rounded">Home</Link>
	
	                <div className="mt-3 mb-1 px-4 text-xs font-semibold text-gray-500 uppercase">Products</div>
	                <Link to={linkTo('/products/property-insurance')} onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-gray-900 hover:bg-gray-50 rounded">Property Insurance</Link>
	                <Link to={linkTo('/products/liability-coverage')} onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-gray-900 hover:bg-gray-50 rounded">Liability Coverage</Link>
	                <Link to={linkTo('/products/employee-benefits')} onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-gray-900 hover:bg-gray-50 rounded">Employee Benefits</Link>
	                <Link to={linkTo('/products/cyber-protection')} onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-gray-900 hover:bg-gray-50 rounded">Cyber Protection</Link>
	                <Link to={linkTo('/products/fleet-insurance')} onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-gray-900 hover:bg-gray-50 rounded">Fleet Insurance</Link>
	                <Link to={linkTo('/products/professional-indemnity')} onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-gray-900 hover:bg-gray-50 rounded">Professional Indemnity</Link>
	                <Link to={linkTo('/products/business-interruption')} onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-gray-900 hover:bg-gray-50 rounded">Business Interruption</Link>
	                <Link to={linkTo('/products/workers-compensation')} onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-gray-900 hover:bg-gray-50 rounded">Workers Compensation</Link>
	                <Link to={linkTo('/products/product-liability')} onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-gray-900 hover:bg-gray-50 rounded">Product Liability</Link>
	
	                <div className="my-3 border-t" />
	                <Link to={linkTo('/decision-tree')} onClick={() => setMobileOpen(false)} className={`block px-4 py-2 text-gray-900 hover:bg-gray-50 rounded`}>Decision Matrix</Link>
	                <Link to={linkTo('/chronicle')} onClick={() => setMobileOpen(false)} className={`block px-4 py-2 text-gray-900 hover:bg-gray-50 rounded`}>The Chronicle</Link>
	                <Link to={linkTo('/about-us')} onClick={() => setMobileOpen(false)} className={`block px-4 py-2 text-gray-900 hover:bg-gray-50 rounded`}>About Us</Link>
	                <Link to={linkTo('/contact-us')} onClick={() => setMobileOpen(false)} className={`block px-4 py-2 text-gray-900 hover:bg-gray-50 rounded`}>Contact Us</Link>
	                <a href="https://yallacompare.com/" target="_blank" rel="noopener noreferrer" className="block px-4 py-2 text-gray-900 hover:bg-gray-50 rounded">Personal Insurance</a>
	              </nav>
	            </SheetContent>
	          </Sheet>
	        </div>
      </div>
    </header>
  );
};

export default StickyNavigation;
